import {graphql} from "@/graphql/generated";
import {mutation, query} from "@/graphql/client";
import {kapitelDateTimeString} from "@/graphql/kapitelTypes";
import {AInesObjectiveSubject, BookingSuggestion, Employee} from "@/graphql/generated/graphql";
import {useGlobalEmitter} from "@/helper/emitter";

export const fetchBookingSuggestion = async (
    begin: kapitelDateTimeString,
    until: kapitelDateTimeString,
    jobsiteId: number,
    withdrawalRequestOriginalEmployeeId?: number,
): Promise<BookingSuggestion | null> => {

    const result = await query(
        graphql(`query GetBookingSuggestion(
            $begin: DateTime!,
            $until: DateTime!,
            $jobsiteId: Int!,
            $withdrawalRequestOriginalEmployeeId: Int 
        ){
            bookingSuggestion(
                begin: $begin, 
                until: $until, 
                jobsiteId: $jobsiteId, 
                withdrawalRequestOriginalEmployeeId: $withdrawalRequestOriginalEmployeeId
            ) {
                seen
                withdrawerName
                withdrawerId
                gapToInstantBookableCheckMessages
                request {
                    id
                    created
                    booking {
                        date
                        begin
                        until
                        shiftType
                        jobsite { ...JobsiteFragment }
                    }
                    message
                }
            }
        }`),
        {begin, until, jobsiteId, withdrawalRequestOriginalEmployeeId: withdrawalRequestOriginalEmployeeId}
    )

    return result?.data?.bookingSuggestion ?? null
}

export const fetchBookingSuggestionsByEmployeeIds = async (
    employeeIds: number[] = [],
    fetchOptions?: RequestInit
): Promise<BookingSuggestion[]> => {
    const result = await query(
        graphql(`query GetBookingSuggestionByEmployeeIds($employeeIds: [Int!]! = []){
            bookingSuggestionByEmployeeIds(employeeIds: $employeeIds) {
                seen
                withdrawerName
                withdrawerId
                gapToInstantBookableCheckMessages
                request {
                    id
                    created
                    message
                    booking {
                        date
                        begin
                        until
                        shiftType
                        jobsite { ...JobsiteFragment }
                    }
                }
            }
        }`),
        {employeeIds},
        {fetchOptions}
    )

    return result?.data?.bookingSuggestionByEmployeeIds ?? []
}

export const fetchBookingSuggestions = async (showSeen = false, showNonMatching = false): Promise<BookingSuggestion[]> => {

    const result = await query(
        graphql(`query GetBookingSuggestions($showSeen: Boolean, $showNonMatching: Boolean){
            bookingSuggestions(showSeen: $showSeen, showNonMatching: $showNonMatching){
                seen
                withdrawerName
                withdrawerId
                gapToInstantBookableCheckMessages
                request {
                    id
                    created
                    message
                    booking {
                        date
                        begin
                        until
                        shiftType
                        jobsite { ...JobsiteFragment }
                    }
                }
            }
        }`),
        {showSeen, showNonMatching}
    )

    return result?.data?.bookingSuggestions ?? []
}

export const setBookingSuggestionToSeen = async (requestId: number): Promise<boolean> => {
    const result = await mutation(
        graphql(`mutation SetBookingSuggestionToSeen($requestId: Int!) {
            setBookingSuggestionToSeen(requestId: $requestId)
        }`),
        {requestId}
    )

    useGlobalEmitter().emit('objective:dirty', AInesObjectiveSubject.BookingWithdrawalRequest)

    return !!result?.data?.setBookingSuggestionToSeen
}

export const claimBookingSuggestion = async (
    begin: kapitelDateTimeString,
    until: kapitelDateTimeString,
    jobsiteId: number,
    withdrawalRequestOriginalEmployeeId?: number,
): Promise<boolean> => {
    const result = await mutation(
        graphql(`mutation ClaimBookingSuggestion(
            $begin: DateTime!,
            $until: DateTime!,
            $jobsiteId: Int!,
            $withdrawalRequestOriginalEmployeeId: Int
        ){
            claimBookingSuggestion(begin: $begin, until: $until, jobsiteId: $jobsiteId, withdrawalRequestOriginalEmployeeId: $withdrawalRequestOriginalEmployeeId)
        }`),
        {begin, until, jobsiteId, withdrawalRequestOriginalEmployeeId:withdrawalRequestOriginalEmployeeId}
    )

    useGlobalEmitter().emit('objective:dirty', AInesObjectiveSubject.BookingWithdrawalRequest)

    return !!result?.data?.claimBookingSuggestion
}

export default {
    fetchBookingSuggestion,
    fetchBookingSuggestions,
    fetchBookingSuggestionsByEmployeeIds,
    setBookingSuggestionToSeen,
    claimBookingSuggestion
}
